import { useEffect } from 'react';

import { IValidateProps } from '@mobiscroll/react/dist/src/core/util/datetime';
import { DateTime } from 'luxon';
import { Navigate, useParams } from 'react-router-dom';

import { useFeature } from 'src/app/FeatureToggle';
import { RootState, useAppDispatch, useAppSelector } from 'src/app/store';
import { DispatchAssignmentRead } from 'src/common/external/bambi-api/bambiApi';
import { LoadingIndicator } from 'src/common/primitives/LoadingIndicator';

import { AssignTripFlow } from '../AssignTripFlow';
import { createEventsFromBreaks } from '../createEventsFromBreaks';
import { createEventsFromTrips } from '../createEventsFromTrips';
import { createEventsFromTripWaitTimes } from '../createEventsFromTripWaitTimes';
import { createResourcesFromAssignments } from '../createResourcesFromAssignments';
import { DispatchCalendar } from '../DispatchCalendar';
import { createInvalidsFromAssignments } from '../DispatchCalendar/createInvalidsFromAssignments';
import AssignmentBreakModal from '../DispatchCalendar/modals/AssignmentBreakModal/AssignmentBreakModal';
import { ConfirmWillCallActivationModal } from '../DispatchCalendar/modals/ConfirmWillCallActivationModal';
import { DeleteBreakModal } from '../DispatchCalendar/modals/DeleteBreakModal';
import { UnassignConfirmationModal } from '../DispatchCalendar/modals/UnassignConfirmationModal';
import { dispatcherSlice } from '../dispatcher.slice';
import { DispatchToolbar } from '../DispatchToolbar';
import { UnassignedTrips } from '../DispatchToolbar/UnassignedTrips';
import { useDispatchRepository } from '../useDispatchRepository';

const selectDispatchResponse = (state: RootState) =>
  state.dispatcher.dispatchResponse;

const ONE_MINUTE_IN_MS = 60000;

function createClockInEventsFromAssignments(
  assignments: DispatchAssignmentRead[]
): IValidateProps[] {
  const clockInEvents: IValidateProps[] = assignments
    .map((assignment) => {
      if (!assignment.clocked_in_at) {
        return {};
      }
      return {
        id: `clock-in-${assignment.id}`,
        start: assignment.clocked_in_at,
        end: assignment.clocked_in_at,
        title: 'Clock In',
        resource: assignment.id,
        assignmentEvent: {
          type: 'clock-in',
        },
      };
    })
    .filter((x) => !!x.start);
  return clockInEvents;
}

function createClockOutEventsFromAssignments(
  assignments: DispatchAssignmentRead[]
): IValidateProps[] {
  const clockInEvents: IValidateProps[] = assignments
    .map((assignment) => {
      if (!assignment.clocked_out_at) {
        return {};
      }
      return {
        id: `clock-out-${assignment.id}`,
        start: assignment.clocked_out_at,
        end: assignment.clocked_out_at,
        title: 'Clock Out',
        resource: assignment.id,
        assignmentEvent: {
          type: 'clock-out',
        },
      };
    })
    .filter((x) => !!x.start);
  return clockInEvents;
}

export function DispatchRoute() {
  const { selectedDate } = useParams();
  const { isEnabled, isLoading: isLoadingFlag } = useFeature(
    'dispatch arrival completion time'
  );

  const dispatch = useAppDispatch();

  const {
    isRunBambiRunInProgress,
    runBambiRunJobId,
    previewEvents,
    isMenuActionInProgress,
  } = useAppSelector((state: RootState) => state.dispatcher);

  const { isLoading: dispatchLoading } = useDispatchRepository({
    refetchOnMountOrArgChange: true,
    pollingInterval: ONE_MINUTE_IN_MS,
    // Mostly for assignment/reassignment when we're previewing the assignment
    // We want to complete the action before we refetch
    // I don't think we have to worry about refetching while previewing a change
    // because that's a layer on top of this data although I guess there's a case
    // where someone else makes the exact assignment that another user is making...
    // Given the polling interval and amount of concurrent dispatchers (usually 1)
    // I think we're ok
    skip: isMenuActionInProgress,
  });

  const dispatchResponse = useAppSelector(selectDispatchResponse);
  const isSidebarOpen = useAppSelector(
    (state: RootState) => state.dispatcher.isDispatchSidebarOpen
  );

  useEffect(() => {
    dispatch(dispatcherSlice.actions.dispatchRouteInit());
  }, [dispatch]);

  useEffect(() => {
    if (selectedDate) {
      dispatch(dispatcherSlice.actions.setSelectedDate(selectedDate));
    }
  }, [dispatch, selectedDate]);

  if (!selectedDate) {
    return (
      <Navigate to={`/dispatch/${DateTime.local().toISODate()}`} replace />
    );
  }
  if (isRunBambiRunInProgress && runBambiRunJobId) {
    return <Navigate to={`/dispatch/${selectedDate}/${runBambiRunJobId}`} />;
  }

  const resources = dispatchResponse?.assignments
    ? createResourcesFromAssignments(dispatchResponse.assignments)
    : [];
  // We only show assigned_trips on cal until run bambi run
  const assignedTripEvents = dispatchResponse?.assigned_trips
    ? createEventsFromTrips(
        dispatchResponse.assigned_trips,
        isEnabled && !isLoadingFlag
      )
    : [];

  const breakEvents = dispatchResponse?.assignments
    ? createEventsFromBreaks(dispatchResponse?.assignments)
    : [];

  const waitTimeEvents = dispatchResponse?.assigned_trips
    ? createEventsFromTripWaitTimes(
        dispatchResponse.assigned_trips,
        isEnabled && !isLoadingFlag
      )
    : [];

  const invalids = dispatchResponse?.assignments
    ? createInvalidsFromAssignments(dispatchResponse.assignments)
    : [];

  const clockInEvents = dispatchResponse?.assignments
    ? createClockInEventsFromAssignments(dispatchResponse.assignments)
    : [];

  const clockOutEvents = dispatchResponse?.assignments
    ? createClockOutEventsFromAssignments(dispatchResponse.assignments)
    : [];
  return (
    <>
      {dispatchLoading || isRunBambiRunInProgress ? (
        <div className="isolate flex h-[85vh] max-h-full max-w-full items-center justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <div className="grid sm:grid-cols-12">
          {isSidebarOpen && (
            <div className="col-span-3">
              <DispatchToolbar>
                <UnassignedTrips />
              </DispatchToolbar>
            </div>
          )}
          <div className={isSidebarOpen ? 'col-span-9 ' : 'col-span-12'}>
            <DispatchCalendar
              resources={resources}
              invalids={invalids}
              events={[
                ...assignedTripEvents,
                ...breakEvents,
                ...waitTimeEvents,
                ...(previewEvents || []),
                ...clockInEvents,
                ...clockOutEvents,
              ]}
            />
          </div>
          <AssignmentBreakModal />
          <AssignTripFlow />
          <ConfirmWillCallActivationModal />
          <DeleteBreakModal />
          <UnassignConfirmationModal />
        </div>
      )}
    </>
  );
}
